import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  addModelBlock,
  addNextBlock,
  draftRoundTypes,
  previewRule,
  programRoundLabel,
  realignToRule,
  ruleExceptions,
  TETRAD_ROUND_TYPES,
  type BlockSetRow,
  type Draft,
  type ProgramFocus,
  type ProgramModel,
  type ProgressionRule,
  type SlotRef,
} from "@heraion/shared";

import { ruleAt, ruleOptions } from "./ruleCache";
import { applyRoundRowPatch } from "./roundEdit";
import type { ProgramEditorV3Props } from "./types";
import { roundLockOf } from "./useRoundLock";
import type { V3Navigation } from "./useV3Navigation";

type Args = Pick<ProgramEditorV3Props, "draft" | "setDraft" | "showToast" | "onUndo"> &
  Partial<Pick<ProgramEditorV3Props, "profile" | "resolveName">> & {
  nav: V3Navigation;
  /** Rounds 0..lockedThrough were executed: never written (-1: none). */
  lockedThrough?: number;
  /** useRoundLock's own test; default, from lockedThrough. */
  isRoundLocked?: (week: number) => boolean;
  /** The cells' unit: a scaled load rounds to its step. Default kg. */
  cellUnit?: ProgramEditorV3Props["cellUnit"];
};

/** How many rounds «+ 4 rounds» adds: one tetrad. */
export const NEXT_BLOCK_ROUNDS = TETRAD_ROUND_TYPES.length;

/** #356: the next block at another focus, from the program's model. */
export type BlockFocus = { model: Omit<ProgramModel, "id">; focus: ProgramFocus };

// The builder's writes (#336 slice 5, brief §5.4b): a rule applied to one
// exercise or to the selection, «Realign», the next block (flow 9), and the
// notice when a typed cell leaves the rule or comes back to it. One setDraft
// each, so one undo step each. The scope of each («Applied to: …», §8.3) is
// said in its toast. The rounds done in a program in progress are left as
// they are (lockedThrough, from the mount's progress marker).
export function useV3ProgressionActions({
  draft,
  setDraft,
  showToast,
  onUndo,
  nav,
  lockedThrough = -1,
  isRoundLocked = roundLockOf(lockedThrough),
  cellUnit,
  profile,
  resolveName,
}: Args) {
  const { t } = useTranslation();
  const opts = ruleOptions(cellUnit);
  const undo = { label: t("program.undo", { defaultValue: "Undo" }), onPress: onUndo };
  const sIdx = nav.dayIdx;

  const applyRule = (refs: readonly SlotRef[], rule: ProgressionRule | readonly ProgressionRule[]) => {
    if (refs.length === 0) return;
    setDraft((d) => previewRule(d, refs, rule, { ...opts, lockedThrough }).draft);
    showToast(
      refs.length === 1
        ? t("editorV3.rule.applied", {
            defaultValue: "Rule changed. Applied to: the rounds that follow it.",
          })
        : t("editorV3.rule.appliedMany", {
            count: refs.length,
            defaultValue: "Rule applied to {{count}} exercises. Applied to: the rounds that follow it.",
          }),
      undo,
    );
  };

  /** One round of the open exercise back on its rule, or the whole row. */
  const realign = (slotIdx: number, week?: number) => {
    if (week !== undefined && isRoundLocked(week)) return;
    const ro = week === undefined ? { ...opts, lockedThrough } : { ...opts, week, lockedThrough };
    setDraft((d) => realignToRule(d, { day: sIdx, slot: slotIdx }, ro));
    showToast(
      week === undefined
        ? t("editorV3.rule.rowRealigned", { defaultValue: "Row realigned with the rule." })
        : t("editorV3.rule.roundRealigned", {
            round: programRoundLabel(week + 1),
            defaultValue: "{{round}} realigned with the rule.",
          }),
      undo,
    );
  };

  /** Flow 9: one more tetrad after the last round; at another focus, its
   *  rows from the model (#356). The sheet's preview reads the same. */
  const effortScale = profile?.effortScale ?? "rir";
  const nextBlock = useCallback(
    (d: Draft, change?: BlockFocus) => {
      const block = { ...ruleOptions(cellUnit), rounds: NEXT_BLOCK_ROUNDS, types: TETRAD_ROUND_TYPES };
      if (!change) return addNextBlock(d, block);
      const named = resolveName ? { exerciseName: (id: string) => resolveName(id) } : {};
      return addModelBlock(d, change.model, change.focus, { ...block, ...named, effortScale });
    },
    [cellUnit, resolveName, effortScale],
  );
  const addBlock = (change?: BlockFocus) => {
    const first = draft.cycleWeeks + 1;
    const res = nextBlock(draft, change);
    if (!res.ok) return;
    setDraft((d) => {
      const next = nextBlock(d, change);
      return next.ok ? next.draft : d;
    });
    showToast(
      t("editorV3.rule.blockAdded", {
        from: programRoundLabel(first),
        to: programRoundLabel(first + NEXT_BLOCK_ROUNDS - 1),
        defaultValue: "{{from}} to {{to}} added.",
      }),
      undo,
    );
  };

  /** Said when a typed cell of round `week`, set `rowIdx`, stops following
   *  the rule, or follows it again. Silent on a row with no rule. */
  const noticeEdit = (slotIdx: number, week: number, rowIdx: number, patch: Partial<BlockSetRow>) => {
    const rec = ruleAt(draft, sIdx, slotIdx, opts.loadQuantum);
    if (rec.kind === "manual") return;
    const after = applyRoundRowPatch(draft, { sIdx, slotIdx, week }, rowIdx, patch).sessions[sIdx]?.slots[slotIdx];
    if (!after) return;
    const hits = (es: typeof rec.exceptions) => es.some((e) => e.week === week && e.set === rowIdx && e.field !== "sets");
    const was = hits(rec.exceptions);
    const now = hits(ruleExceptions(after, rec.rule, draftRoundTypes(draft), opts));
    if (!was && now) {
      showToast(
        t("editorV3.rule.byHand", {
          round: programRoundLabel(week + 1),
          set: rowIdx + 1,
          defaultValue: "{{round}} set {{set}}: written by hand. Applied to: here only.",
        }),
        undo,
      );
    } else if (was && !now) {
      showToast(t("editorV3.rule.followsAgain", { defaultValue: "Follows the rule again." }));
    }
  };

  return { applyRule, realign, nextBlock, addBlock, noticeEdit, lockedThrough };
}

export type V3ProgressionActions = ReturnType<typeof useV3ProgressionActions>;
